import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Stack,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';
import { DeepLinkServices } from './DeepLinkServices';
import { usePermission } from '../../hooks/usePermission';
import ShortLinkDialog from './ShortLinkDialog';
import { formatIst, linkStatus } from './shortLinkDisplay';

const DeepLinkList = () => {
  const navigate = useNavigate();
  const menuDispatch = useDispatch();
  const permission = usePermission('deep-link');
  const [deepUrls, setDeepUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [total, setTotal] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dialog, setDialog] = useState(null);
  const [archiveLink, setArchiveLink] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    menuDispatch({ type: 'menuSelect', payload: 'generate-deep-link' });
  }, [menuDispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPaginationModel(model => model.page === 0 ? model : { ...model, page: 0 });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!permission.canView) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    DeepLinkServices.getDeepUrls({ page: paginationModel.page + 1, limit: paginationModel.pageSize, search })
      .then(response => {
        if (!active) return;
        if (Number(response?.success) !== 1) throw new Error(response?.message || 'Could not load links.');
        const rows = response.data || [];
        setDeepUrls(rows);
        setTotal(response.pagination?.total ?? response.total ?? rows.length);
      }).catch(err => {
        if (active) { setError(err.response?.data?.message || err.message || 'Could not load links.'); setDeepUrls([]); }
      }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [paginationModel, search, refresh, permission.canView]);

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); setNotice('Link copied to clipboard!'); }
    catch { setError('Could not copy the link. Select the URL and copy it manually.'); }
  };

  const archive = async () => {
    setArchiving(true);
    setError('');
    try {
      const response = await DeepLinkServices.archiveDeepUrl(archiveLink._id);
      if (Number(response?.success) !== 1) throw new Error(response?.message || 'Could not archive this link.');
      setArchiveLink(null);
      setRefresh(value => value + 1);
      setNotice('Link archived. Its alias remains reserved.');
    } catch (err) { setError(err.response?.data?.message || err.message || 'Could not archive this link.'); }
    finally { setArchiving(false); }
  };

  const columns = [
    { field: 'title', headerName: 'Title', width: 180 },
    {
      field: 'short_url', headerName: 'Short URL', minWidth: 260, flex: 1,
      renderCell: ({ row }) => <Box sx={{ overflowWrap: 'anywhere' }}>
        {row.short_url ? <><Typography variant="body2">{row.short_url}</Typography><Button size="small" onClick={() => copy(row.short_url)}>Copy short URL</Button></>
          : !row.archived_at && permission.canCreate ? <Button size="small" onClick={() => setDialog({ mode: 'generate', link: row })}>Generate Short URL</Button> : '—'}
      </Box>,
    },
    {
      field: 'full_url',
      headerName: 'Full URL',
      flex: 1,
      minWidth: 360,
      renderCell: (params) => (
        <Box><Typography sx={{ fontSize: '0.7rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>{params.value}</Typography><Button size="small" onClick={() => copy(params.value)}>Copy full URL</Button></Box>
      ),
    },
    { field: 'status', headerName: 'Status', width: 110, renderCell: ({ row }) => <Chip size="small" label={linkStatus(row)} color={linkStatus(row) === 'Active' ? 'success' : 'default'} /> },
    { field: 'expires_at', headerName: 'Expiry (IST)', width: 210, renderCell: ({ value }) => <Typography variant="caption">{formatIst(value)}</Typography> },
    { field: 'actions', headerName: 'Actions', width: 160, sortable: false, renderCell: ({ row }) => !row.archived_at && <Stack direction="row">
      {permission.canEdit && <Button size="small" onClick={() => navigate(`/marketing/generate-deep-link/${encodeURIComponent(row._id)}/edit`)}>Edit</Button>}
      {permission.canDelete && <Button size="small" color="error" onClick={() => setArchiveLink(row)}>Archive</Button>}
    </Stack> },
    {
      field: 'createdByName',
      headerName: 'Created By',
      width: 180,
    },
    {
      field: 'createdAt',
      headerName: 'Created On',
      width: 190,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.65rem' }}>
          {params.value ? formatIst(params.value) : ''}
        </Typography>
      ),
    },
  ];

  if (!permission.canView) return <Alert severity="error">You do not have permission to view deep links.</Alert>;

  return (
    <>
      <PageHeaderLayout headerTitle="Deep Links & Short Links">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', minWidth: 0 }}>
        {permission.canCreate && <Button
          variant="contained"
          size="small"
          startIcon={<AddLinkIcon />}
          onClick={() => navigate('/marketing/generate-deep-link/create')}
        >
          Generate Deep Link
        </Button>}
        {permission.canCreate && <Button size="small" variant="outlined" onClick={() => setDialog({ mode: 'create' })}>Create Short Link</Button>}
        </Box>
      </PageHeaderLayout>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Box sx={{ width: '100%', backgroundColor: '#fff', borderRadius: '.5rem' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
            <TextField
              size="small"
              placeholder="Search deep links..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: '1rem', mr: 1, color: '#777' }} /> }}
              sx={{ width: { xs: '100%', sm: 280 } }}
            />
          </Box>
          <DataGrid
            rows={deepUrls}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={total}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            pageSizeOptions={[5, 10, 20, 50]}
            disableColumnMenu
            disableRowSelectionOnClick
            autoHeight
            getRowHeight={() => 'auto'}
            slots={{
              loadingOverlay: () => <CircularProgress size={24} sx={{ mt: 4 }} />,
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                border: 1,
                borderRight: 0,
                borderTop: 0,
                borderColor: 'lightgrey',
                display: 'flex',
                alignItems: 'center',
                py: 1,
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '2px solid rgba(224, 224, 224, 1)',
                borderTop: '2px solid rgba(224, 224, 224, 1)',
                fontWeight: 600,
                fontSize: '0.75rem',
                minHeight: '2rem',
              },
              '& .MuiDataGrid-columnHeader': {
                borderLeft: '1px solid rgba(224, 224, 224, 1)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                color: '#3e3e3e',
              },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
            }}
          />
        </Box>
      </Box>
      {dialog && <ShortLinkDialog {...dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); setRefresh(value => value + 1); setNotice('Link saved.'); }} />}
      <Dialog open={!!archiveLink} onClose={archiving ? undefined : () => setArchiveLink(null)} fullWidth maxWidth="xs">
        <DialogTitle>Archive link?</DialogTitle>
        <DialogContent>This short link will stop redirecting. Its alias stays reserved and cannot be reused.</DialogContent>
        <DialogActions><Button disabled={archiving} onClick={() => setArchiveLink(null)}>Cancel</Button><Button disabled={archiving} color="error" onClick={archive}>{archiving ? 'Archiving…' : 'Archive'}</Button></DialogActions>
      </Dialog>
      <Snackbar open={!!notice} message={notice} autoHideDuration={3000} onClose={() => setNotice('')} />
    </>
  );
};

export default DeepLinkList;
